import React, {
    useEffect,
    useImperativeHandle,
    useMemo,
    useState,
} from 'react';
import { PDFExportable } from 'waypoint-utils/pdf/PDFExportable';
import { PDFOptions } from '@progress/kendo-drawing/pdf';
import { PDFBuilder } from 'waypoint-utils/pdf/PDFBuilder';
import {
    Upload,
    UploadFileInfo,
    UploadOnAddEvent,
} from '@progress/kendo-react-upload';
import {
    getDocumentUploadById,
    updateDocumentUpload,
    uploadDocument,
} from 'waypoint-requests';
import { PDFDocument } from 'pdf-lib';
import { useGetDocumentUploadByReferenceParams } from 'waypoint-hooks/data-access/useGetDocumentUploadByReferenceParams';
import { css } from 'emotion';
import { LocalizationProvider, loadMessages } from '@progress/kendo-react-intl';
import { Button, Empty, message, Modal, Popconfirm } from 'antd';
import theme from 'config/theme';
import '@progress/kendo-theme-default/dist/all.css';
import { PDFHeaderParams } from 'waypoint-types/report/types';

interface AttachmentDocumentUploadEntityReportWidgetParams {
    widgetId: string;
    isPDFExport: boolean;
    setIsUploading?: (isUploading: boolean) => void;
    isUploading?: boolean;
}

const hideKendoUploadElementsClass = css`
    .k-actions,
    .k-progressbar {
        display: none;
    }
`;

const uploadButtonStyle = css`
    color: ${theme.colors.white} !important;
    background: ${theme.colors.blues.primary} !important;
    border-color: ${theme.colors.blues.primary} !important;
    margin-right: 5px;
`;

const uploadModalButtonStyle = css`
    color: ${theme.colors.white} !important;
    background: ${theme.colors.blues.primary} !important;
    border-color: ${theme.colors.blues.primary} !important;
    width: 75px;
    border-radius: 5px !important;
    margin: 5px 0px 5px 5px;
`;

const uploadModalButtonDisabledStyle = css`
    color: ${theme.colors.white} !important;
    background: ${theme.colors.blues.primary} !important;
    border-color: ${theme.colors.blues.primary} !important;
    width: 100px;
    border-radius: 5px !important;
    margin: 5px 0px 5px 5px;
    opacity: 0.5;
    cursor: not-allowed;
    pointer-events: none;
`;

export const AttachmentDocumentUploadEntityReportWidget = React.forwardRef<
    PDFExportable,
    AttachmentDocumentUploadEntityReportWidgetParams
>(
    (
        {
            widgetId,
            isPDFExport,
            setIsUploading,
            isUploading,
        }: AttachmentDocumentUploadEntityReportWidgetParams,
        ref
    ) => {
        const [existingAttachment, setExistingAttachment] = useState<
            string | undefined
        >(undefined);
        const [pdfDoc, setPdfDoc] = useState<PDFDocument | null>(null);
        const [files, setFiles] = useState<UploadFileInfo[]>([]);
        const [showUploadButtons, setShowUploadButtons] =
            useState<boolean>(false);
        const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
        const [documentFetchError, setDocumentFetchError] =
            useState<boolean>(false);

        const {
            data: existingDocumentUpload,
            isError: existingDocumentUploadError,
            isLoading: isValidatingDocumentUpload,
            mutate,
        } = useGetDocumentUploadByReferenceParams(
            'entity_report_widget',
            widgetId
        );

        useEffect(() => {
            setDocumentFetchError(existingDocumentUploadError);
        }, [existingDocumentUploadError]);

        useMemo(() => {
            if (documentFetchError && !isPDFExport) {
                message.error('Failed to fetch uploaded document');
            }
        }, [documentFetchError, isPDFExport]);

        // Needed to customize select button text
        const customMessages = {
            upload: {
                select: 'Select File',
            },
        };
        loadMessages(customMessages, 'custom');

        useImperativeHandle(ref, () => ({
            exportToPDF: async (
                options?: PDFOptions,
                pdfTemplateParams?: PDFHeaderParams
            ): Promise<PDFBuilder> => {
                const pdfBuilder = new PDFBuilder();
                if (!existingDocumentUpload?.id || documentFetchError) {
                    await pdfBuilder.addElementById(
                        widgetId,
                        {},
                        1,
                        pdfTemplateParams
                    );
                } else {
                    pdfDoc &&
                        (await pdfBuilder.addAttachment(
                            pdfDoc,
                            widgetId,
                            pdfTemplateParams
                        ));
                }

                return pdfBuilder;
            },
            isReadyToExport(): boolean {
                if (
                    existingDocumentUpload?.id &&
                    !documentFetchError &&
                    !isValidatingDocumentUpload
                ) {
                    return !!pdfDoc;
                }
                return !isValidatingDocumentUpload;
            },
        }));

        const setExistingDocumentUpload = async () => {
            try {
                const fileBuffer = await getDocumentUploadById(
                    existingDocumentUpload?.id ?? ''
                );
                const pdfDoc = await PDFDocument.load(fileBuffer);
                pdfDoc.setTitle(existingDocumentUpload?.document_url ?? '');
                setPdfDoc(pdfDoc);
                const rawPDF = await pdfDoc.save();
                const docUrl = URL.createObjectURL(
                    new Blob([rawPDF], { type: 'application/pdf' })
                );
                setExistingAttachment(docUrl);
                setDocumentFetchError(false);
            } catch {
                setPdfDoc(null);
                setDocumentFetchError(true);
            }
        };

        useEffect(() => {
            if (existingDocumentUpload?.id) {
                setExistingDocumentUpload();
            }
        }, [existingDocumentUpload]);

        const onUploadFile = () => {
            if (!files || !files[0].getRawFile) {
                return;
            }
            const documentToUpload = new FormData();
            documentToUpload.append('upload', files[0].getRawFile());
            mutate(
                async () => {
                    try {
                        setIsUploading && setIsUploading(true);
                        const { data: uploadResult } =
                            existingDocumentUpload?.id
                                ? await updateDocumentUpload(
                                      documentToUpload,
                                      existingDocumentUpload.id
                                  )
                                : await uploadDocument(
                                      documentToUpload,
                                      'entity_report_widget',
                                      widgetId
                                  );

                        message.success('Document uploaded successfully');
                        setFiles([]);
                        setShowUploadButtons(false);
                        setIsModalOpen(false);
                        setIsUploading && setIsUploading(false);
                        return uploadResult;
                    } catch (e) {
                        setIsUploading && setIsUploading(false);
                        message.error('Error uploading document');
                    }
                },
                {
                    rollbackOnError: true,
                    populateCache: true,
                    revalidate: false,
                }
            );
        };

        const onRemove = () => {
            setFiles([]);
            setShowUploadButtons(false);
        };

        const onAddFile = (event: UploadOnAddEvent) => {
            if (!event.newState[0] || !event.newState[0].getRawFile) {
                return;
            }
            setFiles(event.newState);
            setShowUploadButtons(true);
        };

        const onCloseModal = () => {
            setIsModalOpen(false);
            onRemove();
        };

        if (isPDFExport) {
            if (!existingDocumentUpload?.id) {
                return (
                    <div id={widgetId}>
                        <Empty description="Attachment has not been uploaded" />
                    </div>
                );
            }
            if (documentFetchError) {
                return (
                    <div id={widgetId}>
                        <Empty description="Error fetching attachment" />
                    </div>
                );
            }
        }

        const PDF_VIEWER_HEIGHT = `${window.innerHeight - 275}px`;

        return (
            <>
                <div
                    style={{
                        display: 'flex',
                        fontSize: '14px',
                        alignItems: 'center',
                        marginBottom: '10px',
                    }}
                >
                    <Button
                        type="primary"
                        className={uploadButtonStyle}
                        onClick={() => setIsModalOpen(true)}
                    >
                        Upload Attachment
                    </Button>
                    <span>{'(Max: 1 PDF File)'}</span>
                    {existingDocumentUpload?.id && !documentFetchError && (
                        <div
                            style={{
                                marginRight: '5px',
                                display: 'flex',
                                flex: '1',
                                justifyContent: 'end',
                            }}
                        >
                            {`Uploaded File: ${existingDocumentUpload.document_url}`}
                        </div>
                    )}
                </div>
                <Modal
                    data-test="report-widget-modal"
                    centered={true}
                    width="600px"
                    bodyStyle={{ height: '200px' }}
                    open={isModalOpen}
                    maskClosable={false}
                    footer={null}
                    title={'Upload Attachment (Max. 1 File, PDF Only)'}
                    onCancel={onCloseModal}
                    closable={!isUploading}
                >
                    <LocalizationProvider language="custom">
                        <Upload
                            className={hideKendoUploadElementsClass}
                            batch={false}
                            multiple={false}
                            accept="application/pdf"
                            files={files}
                            withCredentials={false}
                            autoUpload={false}
                            onAdd={onAddFile}
                            onRemove={onRemove}
                        />
                    </LocalizationProvider>
                    {showUploadButtons && (
                        <div
                            style={{
                                marginTop: '15px',
                                marginBottom: '5px',
                                height: '25px',
                                display: 'flex',
                                justifyContent: 'end',
                            }}
                        >
                            <Popconfirm
                                disabled={!existingDocumentUpload?.id}
                                placement="topRight"
                                title={`This action will delete the previous attachment. Are you sure you wish to proceed?`}
                                onConfirm={onUploadFile}
                                onCancel={() => {}}
                                okType="danger"
                                okText="Upload"
                                cancelText="Cancel"
                            >
                                <Button
                                    type="primary"
                                    className={
                                        isUploading
                                            ? uploadModalButtonDisabledStyle
                                            : uploadModalButtonStyle
                                    }
                                    onClick={
                                        !existingDocumentUpload?.id
                                            ? onUploadFile
                                            : undefined
                                    }
                                >
                                    {isUploading ? 'Uploading...' : 'Upload'}
                                </Button>
                            </Popconfirm>
                        </div>
                    )}
                </Modal>
                {!!existingAttachment && !isPDFExport && (
                    <iframe
                        title="attachment"
                        src={existingAttachment}
                        width="100%"
                        height={PDF_VIEWER_HEIGHT}
                    />
                )}
            </>
        );
    }
);
